import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import {
  getDashboardDataRepository,
  getDropdownDataRepository,
  createGiftCertificatesRepository,
  getGiftCertificatesRepository,
  updateGCStatusRepository,
  getGCDetailsRepository,
  createStoreGroupRepository,
  getStoreGroupsRepository,
  deleteGiftCertificateRepository,
  getUserStoresRepository,
  deleteStoreGroupRepository,
  getStoreGroupsDetailedRepository,
  updateStoreGroupRepository,
  checkAdminAccessRepository,
  getUserPermissionsRepository,
  getValidNextStatusesRepository,
  requestStatusChangeRepository,
  getPendingRequestsRepository,
  reviewStatusRequestRepository,
  checkPendingApprovalRepository,
  getAvailableStatusesRepository,
  bulkApproveRequestsRepository,
  getActiveStoresRepository,
  getGCAttachmentsRepository,
} from "features/ms6/data/ms6.repository";
import {
  CreateGiftCertificateParam,
  UpdateGCStatusParam,
  CreateStoreGroupParam,
  GiftCertificateFilters,
} from "features/ms6/core/ms6.param";

export enum GiftCertificateState {
  initial = "initial",
  inProgress = "inProgress",
  success = "success",
  fail = "fail",
}

export interface GiftCertificate {
  id: number;
  serial_number: string;
  type: "customer" | "sponsorship" | "support";
  store_id?: number;
  store_group_id?: number;
  company_id?: number;
  expiry_date?: string;
  redeemed_at_store_id?: number;
  current_status:
    | "Created"
    | "Released"
    | "Receive Approval"
    | "Received"
    | "Sold Approval"
    | "Sold"
    | "Redeem Approval"
    | "Redeemed"
    | "Expired";
  created_by: number;
  created_at: string;
  store_name?: string;
  group_name?: string;
  company_name?: string;
  redemption_date?: string;
  redeemed_at_store_name?: string;
  first_name?: string;
  last_name?: string;
  receiving_request_date?: string;
  receiving_approval_date?: string;
  selling_request_date?: string;
  selling_approval_date?: string;
  redemption_request_date?: string;
  redemption_approval_date?: string;
}

export interface StatusHistory {
  id: number;
  gc_id: number;
  status: string;
  changed_by: number;
  changed_at: string;
  notes?: string;
  first_name: string;
  last_name: string;
}

export interface StoreGroup {
  id: number;
  group_name: string;
  store_count: number;
  created_by: number;
  created_at: string;
  first_name: string;
  last_name: string;
  stores?: Array<{
    id: number;
    name: string;
    address: string;
  }>;
}

export interface DropdownData {
  stores: Array<{ id: number; name: string; address: string }>;
  companies: Array<{ id: number; name: string }>;
  store_groups: StoreGroup[];
}

export interface DashboardStats {
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  recent_week: number;
}

export interface UserPermissions {
  is_admin: boolean;
  is_stock_clerk: boolean;
  is_warehouse?: boolean;
  is_gc?: boolean;
  can_approve_status: boolean;
  can_see_redeem_approvals?: boolean;
  can_approve_redemption?: boolean;
  stock_permissions: Array<{
    permission_id: number;
    permission_name: string;
  }>;
}

export interface GCAttachment {
  id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  uploaded_by: number;
  uploaded_at: string;
  first_name: string;
  last_name: string;
}

export interface StatusChangeRequest {
  id: number;
  gc_id: number;
  current_status: string;
  requested_status: string;
  requested_by: number;
  request_notes?: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  serial_number: string;
  type: "customer" | "sponsorship" | "support";
  store_name?: string;
  group_name?: string;
  company_name?: string;
  requester_first_name?: string;
  requester_last_name?: string;
}

// UPDATED: Enhanced InitialState interface
interface InitialState {
  isAdmin: boolean;
  userStores: Array<{ id: number; name: string; address: string }>;
  status: GiftCertificateState;
  message: string;
  availableStatuses: string[];
  pendingRequests: StatusChangeRequest[];
  certificates: GiftCertificate[];
  currentCertificate: GiftCertificate | null;
  statusHistory: StatusHistory[];
  storeGroups: StoreGroup[];
  dropdownData: DropdownData | null;
  dashboardStats: DashboardStats | null;
  lastCreated: {
    customer: GiftCertificate | null;
    sponsorship: GiftCertificate | null;
    support: GiftCertificate | null;
  };
  userPermissions: UserPermissions | null;
  validNextStatuses: string[];
  canSeeCreatedGCs: boolean;
  activeStores: Array<{ id: number; name: string; address: string }>;
  gcAttachments: GCAttachment[];
}

// UPDATED: Enhanced initialState
const initialState: InitialState = {
  isAdmin: false,
  status: GiftCertificateState.initial,
  userStores: [],
  message: "",
  availableStatuses: [],
  pendingRequests: [],
  certificates: [],
  currentCertificate: null,
  statusHistory: [],
  storeGroups: [],
  dropdownData: null,
  dashboardStats: null,
  lastCreated: {
    customer: null,
    sponsorship: null,
    support: null,
  },
  userPermissions: null,
  validNextStatuses: [],
  canSeeCreatedGCs: true,
  activeStores: [],
  gcAttachments: [],
};

// Async Thunks
export const checkAdminAccess = createAsyncThunk(
  "giftCertificate/checkAdminAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkAdminAccessRepository();
      return {
        is_admin: response.data.is_admin, // Now includes both admin and GC
        is_gc: response.data.is_gc,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue({
          is_admin: false,
          is_gc: false,
        });
      }
      return rejectWithValue({
        is_admin: false,
        is_gc: false,
      });
    }
  }
);

export const getUserStores = createAsyncThunk(
  "giftCertificate/getUserStores",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserStoresRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch user stores"
        );
      }
      return rejectWithValue("Failed to fetch user stores");
    }
  }
);

export const getUserPermissions = createAsyncThunk(
  "giftCertificate/getUserPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserPermissionsRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch user permissions"
        );
      }
      return rejectWithValue("Failed to fetch user permissions");
    }
  }
);

export const getActiveStores = createAsyncThunk(
  "giftCertificate/getActiveStores",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getActiveStoresRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch active stores"
        );
      }
      return rejectWithValue("Failed to fetch active stores");
    }
  }
);

export const getGCAttachments = createAsyncThunk(
  "giftCertificate/getAttachments",
  async (gcId: number, { rejectWithValue }) => {
    try {
      const response = await getGCAttachmentsRepository(gcId);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch attachments"
        );
      }
      return rejectWithValue("Failed to fetch attachments");
    }
  }
);

export const getValidNextStatuses = createAsyncThunk(
  "giftCertificate/getValidNextStatuses",
  async (currentStatus: string, { rejectWithValue }) => {
    try {
      const response = await getValidNextStatusesRepository(currentStatus);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue([]);
      }
      return rejectWithValue([]);
    }
  }
);

export const getAvailableStatuses = createAsyncThunk(
  "giftCertificate/getAvailableStatuses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAvailableStatusesRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue([]);
      }
      return rejectWithValue([]);
    }
  }
);

export const requestStatusChange = createAsyncThunk(
  "giftCertificate/requestStatusChange",
  async (
    requestData: { gc_id: number; status: string; notes?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await requestStatusChangeRepository(requestData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to request status change"
        );
      }
      return rejectWithValue("Failed to request status change");
    }
  }
);

export const getPendingRequests = createAsyncThunk(
  "giftCertificate/getPendingRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPendingRequestsRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch pending requests"
        );
      }
      return rejectWithValue("Failed to fetch pending requests");
    }
  }
);

export const reviewStatusRequest = createAsyncThunk(
  "giftCertificate/reviewStatusRequest",
  async (
    reviewData: {
      request_id: number;
      action: "approved" | "rejected";
      review_notes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await reviewStatusRequestRepository(reviewData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to review request"
        );
      }
      return rejectWithValue("Failed to review request");
    }
  }
);

export const bulkApproveRequests = createAsyncThunk(
  "giftCertificate/bulkApproveRequests",
  async (
    approvalData: {
      request_ids: number[];
      review_notes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await bulkApproveRequestsRepository(approvalData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to bulk approve requests"
        );
      }
      return rejectWithValue("Failed to bulk approve requests");
    }
  }
);

export const getDashboardData = createAsyncThunk(
  "giftCertificate/getDashboardData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDashboardDataRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch dashboard data"
        );
      }
      return rejectWithValue("Failed to fetch dashboard data");
    }
  }
);

export const getDropdownData = createAsyncThunk(
  "giftCertificate/getDropdownData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDropdownDataRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch dropdown data"
        );
      }
      return rejectWithValue("Failed to fetch dropdown data");
    }
  }
);

export const createGiftCertificates = createAsyncThunk(
  "giftCertificate/create",
  async (gcData: CreateGiftCertificateParam, { rejectWithValue }) => {
    try {
      const response = await createGiftCertificatesRepository(gcData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to create gift certificates"
        );
      }
      return rejectWithValue("Failed to create gift certificates");
    }
  }
);

// UPDATED: Enhanced getGiftCertificates thunk
export const getGiftCertificates = createAsyncThunk(
  "giftCertificate/list",
  async (filters: GiftCertificateFilters = {}, { rejectWithValue }) => {
    try {
      const response = await getGiftCertificatesRepository(filters);
      return {
        certificates: response.data.data,
        can_see_created_gcs: response.data.can_see_created_gcs ?? true,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch gift certificates"
        );
      }
      return rejectWithValue("Failed to fetch gift certificates");
    }
  }
);

export const updateGCStatus = createAsyncThunk(
  "giftCertificate/updateStatus",
  async (
    statusData: UpdateGCStatusParam & { redeemed_at_store_id?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateGCStatusRepository(statusData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update status"
        );
      }
      return rejectWithValue("Failed to update status");
    }
  }
);

export const checkPendingApproval = createAsyncThunk(
  "giftCertificate/checkPendingApproval",
  async (gcId: number, { rejectWithValue }) => {
    try {
      const response = await checkPendingApprovalRepository(gcId);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to check pending approval"
        );
      }
      return rejectWithValue("Failed to check pending approval");
    }
  }
);

export const getGCDetails = createAsyncThunk(
  "giftCertificate/details",
  async (gcId: number, { rejectWithValue }) => {
    try {
      const response = await getGCDetailsRepository(gcId);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch gift certificate details"
        );
      }
      return rejectWithValue("Failed to fetch gift certificate details");
    }
  }
);

export const createStoreGroup = createAsyncThunk(
  "giftCertificate/createStoreGroup",
  async (groupData: CreateStoreGroupParam, { rejectWithValue }) => {
    try {
      const response = await createStoreGroupRepository(groupData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to create store group"
        );
      }
      return rejectWithValue("Failed to create store group");
    }
  }
);

export const getStoreGroups = createAsyncThunk(
  "giftCertificate/getStoreGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getStoreGroupsRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch store groups"
        );
      }
      return rejectWithValue("Failed to fetch store groups");
    }
  }
);

export const deleteGiftCertificate = createAsyncThunk(
  "giftCertificate/delete",
  async (gcId: number, { rejectWithValue }) => {
    try {
      const response = await deleteGiftCertificateRepository(gcId);
      return { ...response.data, deletedId: gcId };
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to delete gift certificate"
        );
      }
      return rejectWithValue("Failed to delete gift certificate");
    }
  }
);

export const getStoreGroupsDetailed = createAsyncThunk(
  "giftCertificate/getStoreGroupsDetailed",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getStoreGroupsDetailedRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to fetch detailed store groups"
        );
      }
      return rejectWithValue("Failed to fetch detailed store groups");
    }
  }
);

export const deleteStoreGroup = createAsyncThunk(
  "giftCertificate/deleteStoreGroup",
  async (groupId: number, { rejectWithValue }) => {
    try {
      const response = await deleteStoreGroupRepository(groupId);
      return { ...response.data, deletedGroupId: groupId };
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to delete store group"
        );
      }
      return rejectWithValue("Failed to delete store group");
    }
  }
);

export const updateStoreGroup = createAsyncThunk(
  "giftCertificate/updateStoreGroup",
  async (
    groupData: { group_id: number; group_name: string; store_ids: number[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateStoreGroupRepository(groupData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update store group"
        );
      }
      return rejectWithValue("Failed to update store group");
    }
  }
);

// UPDATED: Enhanced slice with new reducers
const giftCertificateSlice = createSlice({
  name: "giftCertificate",
  initialState,
  reducers: {
    resetGiftCertificate: (state) => {
      state.status = GiftCertificateState.initial;
      state.message = "";
    },
    clearCurrentCertificate: (state) => {
      state.currentCertificate = null;
      state.statusHistory = [];
      state.gcAttachments = [];
    },
  },
  extraReducers: (builder) => {
    // Admin Access
    builder
      .addCase(checkAdminAccess.fulfilled, (state, action) => {
        state.isAdmin = action.payload.is_admin; // Now true for both admin and GC users
        // Handle GC user info
        if (state.userPermissions) {
          state.userPermissions.is_gc = action.payload.is_gc;
        }
      })
      .addCase(checkAdminAccess.rejected, (state) => {
        state.isAdmin = false;
      })
      .addCase(getActiveStores.fulfilled, (state, action) => {
        state.activeStores = action.payload;
      });

    // User Stores
    builder.addCase(getUserStores.fulfilled, (state, action) => {
      state.userStores = action.payload;
    });

    builder
      .addCase(getGCAttachments.fulfilled, (state, action) => {
        state.gcAttachments = action.payload || [];
      })
      .addCase(getGCAttachments.rejected, (state, action) => {
        state.gcAttachments = [];
      });

    // Available Statuses
    builder.addCase(getAvailableStatuses.fulfilled, (state, action) => {
      state.availableStatuses = action.payload;
    });

    // User Permissions
    builder
      .addCase(getUserPermissions.fulfilled, (state, action) => {
        state.userPermissions = action.payload;
      })
      .addCase(getValidNextStatuses.fulfilled, (state, action) => {
        state.validNextStatuses = action.payload;
      });

    // Status Change Requests
    builder
      .addCase(requestStatusChange.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(requestStatusChange.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      })
      .addCase(getPendingRequests.fulfilled, (state, action) => {
        state.pendingRequests = action.payload;
      })
      .addCase(reviewStatusRequest.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(reviewStatusRequest.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // Bulk Approve Requests
    builder
      .addCase(bulkApproveRequests.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(bulkApproveRequests.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // Dashboard Data
    builder
      .addCase(getDashboardData.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(getDashboardData.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.dashboardStats = action.payload.stats;
        state.lastCreated = {
          customer: action.payload.last_created.customer || null,
          sponsorship: action.payload.last_created.sponsorship || null,
          support: action.payload.last_created.support || null,
        };
      })
      .addCase(getDashboardData.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // Dropdown Data
    builder.addCase(getDropdownData.fulfilled, (state, action) => {
      state.dropdownData = action.payload;
    });

    // Create Gift Certificates
    builder
      .addCase(createGiftCertificates.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(createGiftCertificates.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(createGiftCertificates.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // UPDATED: Get Gift Certificates with permission handling
    builder.addCase(getGiftCertificates.fulfilled, (state, action) => {
      state.certificates = action.payload.certificates;
      if (action.payload.can_see_created_gcs !== undefined) {
        state.canSeeCreatedGCs = action.payload.can_see_created_gcs;
      }
    });

    // Update Status
    builder
      .addCase(updateGCStatus.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(updateGCStatus.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(updateGCStatus.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // Get GC Details
    builder.addCase(getGCDetails.fulfilled, (state, action) => {
      state.currentCertificate = action.payload.certificate;
      state.statusHistory = action.payload.history;
    });

    // Delete Gift Certificate
    builder
      .addCase(deleteGiftCertificate.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(deleteGiftCertificate.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
        // Remove the deleted certificate from the list
        state.certificates = state.certificates.filter(
          (cert) => cert.id !== action.payload.deletedId
        );
      })
      .addCase(deleteGiftCertificate.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    // Store Groups
    builder
      .addCase(createStoreGroup.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(createStoreGroup.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(createStoreGroup.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });

    builder.addCase(getStoreGroups.fulfilled, (state, action) => {
      state.storeGroups = action.payload;
    });

    builder
      .addCase(getStoreGroupsDetailed.fulfilled, (state, action) => {
        state.storeGroups = action.payload;
      })
      .addCase(deleteStoreGroup.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(deleteStoreGroup.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
        state.storeGroups = state.storeGroups.filter(
          (group) => group.id !== action.payload.deletedGroupId
        );
      })
      .addCase(deleteStoreGroup.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      })
      .addCase(updateStoreGroup.pending, (state) => {
        state.status = GiftCertificateState.inProgress;
      })
      .addCase(updateStoreGroup.fulfilled, (state, action) => {
        state.status = GiftCertificateState.success;
        state.message = action.payload.message;
      })
      .addCase(updateStoreGroup.rejected, (state, action) => {
        state.status = GiftCertificateState.fail;
        state.message = action.payload as string;
      });
  },
});

export const { resetGiftCertificate, clearCurrentCertificate } =
  giftCertificateSlice.actions;

export const selectGiftCertificate = (state: RootState) =>
  state.giftCertificate;

export default giftCertificateSlice.reducer;
